import React, { useState } from "react";

interface CharacterKeyboardProps {
  value: string;
  onValueChange: (value: string) => void;
  onClose?: () => void;
}

// Character layout rows (lowercase base — case applied at render/click time)
const CHAR_ROW_1 = ["q", "w", "e", "r", "t", "y", "u", "i", "o", "p"];
const CHAR_ROW_2 = ["a", "s", "d", "f", "g", "h", "j", "k", "l"];
const CHAR_ROW_3_LETTERS = ["z", "x", "c", "v", "b", "n", "m"];

// Number/symbol layout rows (mirrors legacy `alphanumeric` layout)
const NUM_ROW_1 = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "0"];
const NUM_ROW_2 = ["-", "/", ":", "(", ")", "$", "&", "@", '"'];
const NUM_ROW_3 = [".", ",", "?", "!", "'"];

const baseButtonStyles = {
  WebkitUserSelect: "none" as const,
  MozUserSelect: "none" as const,
  msUserSelect: "none" as const,
  userSelect: "none" as const,
  WebkitTouchCallout: "none" as const,
  touchAction: "manipulation" as const,
  WebkitTapHighlightColor: "transparent",
};

const btnClass =
  "flex-1 bg-keyboard-number-bg text-keyboard-number-text font-bold h-[38px] px-1 active:scale-95 cursor-pointer outline-none border-none text-sm";
const actionBtnClass =
  "bg-keyboard-chip-bg text-keyboard-chip-text font-bold h-[38px] px-3 active:scale-95 cursor-pointer outline-none border-none text-sm";

const CharacterKeyboard: React.FC<CharacterKeyboardProps> = ({
  value,
  onValueChange,
  onClose,
}) => {
  const [isUpperCase, setIsUpperCase] = useState(false);
  const [isNumberMode, setIsNumberMode] = useState(false);

  const applyCase = (char: string) =>
    isUpperCase ? char.toUpperCase() : char;

  const handleChar = (char: string) => onValueChange(value + char);
  const handleDelete = () => onValueChange(value.slice(0, -1));
  const handleSpace = () => onValueChange(value + " ");
  const handleClearAll = () => onValueChange("");
  const handleToggleCase = () => setIsUpperCase((prev) => !prev);
  const handleToggleMode = () => setIsNumberMode((prev) => !prev);

  const renderCharRows = () => (
    <>
      {[CHAR_ROW_1, CHAR_ROW_2].map((row, i) => (
        <div key={i} className="flex gap-[3px]">
          {row.map((char) => (
            <button
              key={char}
              onClick={() => handleChar(applyCase(char))}
              style={baseButtonStyles}
              className={btnClass}
            >
              {applyCase(char)}
            </button>
          ))}
        </div>
      ))}
      <div className="flex gap-[3px]">
        <button
          onClick={handleToggleCase}
          style={baseButtonStyles}
          className={`${actionBtnClass} min-w-[36px]`}
        >
          {isUpperCase ? "▼" : "▲"}
        </button>
        {CHAR_ROW_3_LETTERS.map((char) => (
          <button
            key={char}
            onClick={() => handleChar(applyCase(char))}
            style={baseButtonStyles}
            className={btnClass}
          >
            {applyCase(char)}
          </button>
        ))}
        <button
          onClick={handleDelete}
          style={baseButtonStyles}
          className={`${actionBtnClass} min-w-[36px]`}
        >
          ⌫
        </button>
      </div>
    </>
  );

  const renderNumRows = () => (
    <>
      <div className="flex gap-[3px]">
        {NUM_ROW_1.map((char) => (
          <button key={char} onClick={() => handleChar(char)} style={baseButtonStyles} className={btnClass}>
            {char}
          </button>
        ))}
      </div>
      <div className="flex gap-[3px]">
        {NUM_ROW_2.map((char) => (
          <button key={char} onClick={() => handleChar(char)} style={baseButtonStyles} className={btnClass}>
            {char}
          </button>
        ))}
      </div>
      <div className="flex gap-[3px]">
        {NUM_ROW_3.map((char) => (
          <button key={char} onClick={() => handleChar(char)} style={baseButtonStyles} className={btnClass}>
            {char}
          </button>
        ))}
        <button onClick={handleDelete} style={baseButtonStyles} className={`${actionBtnClass} min-w-[36px]`}>
          ⌫
        </button>
      </div>
    </>
  );

  const renderBottomRow = () => (
    <div className="flex gap-[3px]">
      <button onClick={handleToggleMode} style={baseButtonStyles} className={`${actionBtnClass} min-w-[48px]`}>
        {isNumberMode ? "abc" : "123"}
      </button>
      <button onClick={handleSpace} style={baseButtonStyles} className={`flex-1 ${actionBtnClass}`}>
        space
      </button>
      <button onClick={handleClearAll} style={baseButtonStyles} className={`${actionBtnClass} min-w-[64px]`}>
        clear all
      </button>
    </div>
  );

  return (
    <div
      className="w-full flex flex-col gap-[3px] bg-keyboard-bg select-none"
      style={{
        WebkitFontSmoothing: "antialiased",
        MozOsxFontSmoothing: "grayscale",
        touchAction: "manipulation",
        WebkitTapHighlightColor: "transparent",
      }}
    >
      <div className="flex gap-[3px]">
        <div className="flex flex-col gap-[3px] flex-1">
          {isNumberMode ? renderNumRows() : renderCharRows()}
          {renderBottomRow()}
        </div>
        {onClose && (
          <button
            onClick={onClose}
            style={baseButtonStyles}
            className="self-stretch bg-keyboard-number-bg text-keyboard-number-text font-bold px-4 active:scale-95 cursor-pointer outline-none border-none min-w-[48px]"
          >
            OK
          </button>
        )}
      </div>
    </div>
  );
};

export default CharacterKeyboard;
